import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Education {
  degree: string;
  institution: string;
  year: string;
}

interface BarAdmission {
  state: string;
  year: string;
}

interface AttorneyBioProps {
  name: string;
  title: string;
  image: string;
  imageAlt: string;
  bio: string[];
  education: Education[];
  barAdmissions: BarAdmission[];
  yearsExperience: number;
  specializations: string[];
}

const AttorneyBio: React.FC<AttorneyBioProps> = ({
  name,
  title,
  image,
  imageAlt,
  bio,
  education,
  barAdmissions,
  yearsExperience,
  specializations,
}) => {
  return (
    <section className="py-20 lg:py-28 bg-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-serif text-3xl lg:text-4xl font-bold text-primary mb-4">
            Meet Our Founder
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Leading with integrity, expertise, and unwavering commitment to justice
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div className="relative">
            <div className="aspect-[3/4] rounded-2xl overflow-hidden shadow-elevation-sm">
              <AppImage
                src={image}
                alt={imageAlt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <h3 className="font-serif text-3xl font-bold text-primary mb-2">
                {name}
              </h3>
              <p className="text-lg text-accent font-medium mb-6">{title}</p>
              <div className="space-y-4">
                {bio.map((paragraph, index) => (
                  <p key={index} className="text-text-secondary leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="bg-background rounded-xl p-6 border border-border">
                <div className="flex items-center gap-3 mb-4">
                  <Icon name="AcademicCapIcon" size={24} className="text-accent" />
                  <h4 className="font-semibold text-primary">Education</h4>
                </div>
                <div className="space-y-3">
                  {education.map((edu, index) => (
                    <div key={index}>
                      <div className="font-medium text-sm text-primary">
                        {edu.degree}
                      </div>
                      <div className="text-sm text-text-secondary">
                        {edu.institution}
                      </div>
                      <div className="text-xs text-text-secondary">{edu.year}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-background rounded-xl p-6 border border-border">
                <div className="flex items-center gap-3 mb-4">
                  <Icon name="ScaleIcon" size={24} className="text-accent" />
                  <h4 className="font-semibold text-primary">Bar Admissions</h4>
                </div>
                <div className="space-y-3">
                  {barAdmissions.map((bar, index) => (
                    <div key={index}>
                      <div className="font-medium text-sm text-primary">
                        {bar.state}
                      </div>
                      <div className="text-xs text-text-secondary">
                        Admitted {bar.year}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-background rounded-xl p-6 border border-border">
              <div className="flex items-center gap-3 mb-4">
                <Icon name="BriefcaseIcon" size={24} className="text-accent" />
                <h4 className="font-semibold text-primary">Practice Areas</h4>
              </div>
              <div className="flex flex-wrap gap-2">
                {specializations.map((spec, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-surface text-sm font-medium text-primary rounded-lg border border-border"
                  >
                    {spec}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AttorneyBio;